import { useMemo } from "react";

/**
 * Sparkle/Stars background animation — inspired by xAI's cosmic aesthetic.
 *
 * Renders a fixed number of ⭐ ✦ ✧ glyphs at random positions, each with
 * an independent twinkle + drift CSS animation. Pure CSS, no JS animation
 * loop, GPU-accelerated, accessibility-friendly (respects reduced motion).
 *
 * Drop it inside any container with `position: relative` (or absolute) and
 * `overflow: hidden`. The component is `pointer-events: none` so it never
 * blocks clicks.
 *
 * Props:
 *   count   — number of sparkles (default 30)
 *   color   — base color of the glyphs (default rgba white)
 *   minSize — px (default 8)
 *   maxSize — px (default 18)
 */
const Sparkles = ({
  count = 30,
  color = "rgba(255,255,255,0.85)",
  minSize = 8,
  maxSize = 18,
}) => {
  // Compute positions / animation parameters once on mount.
  const sparkles = useMemo(() => {
    const glyphs = ["✦", "✧", "★", "⋆", "✺"];
    return Array.from({ length: count }).map((_, i) => {
      const size = minSize + Math.random() * (maxSize - minSize);
      const left = Math.random() * 100; // %
      const top = Math.random() * 100; // %
      const duration = 2.5 + Math.random() * 4; // s
      const delay = Math.random() * 5; // s
      const driftX = (Math.random() - 0.5) * 30; // px
      const driftY = (Math.random() - 0.5) * 30; // px
      const glyph = glyphs[i % glyphs.length];
      return { id: i, size, left, top, duration, delay, driftX, driftY, glyph };
    });
  }, [count, minSize, maxSize]);

  return (
    <>
      <style>{`
        @keyframes sparkle-twinkle {
          0%, 100% { opacity: 0; transform: scale(0.4) rotate(0deg); }
          50% { opacity: 1; transform: scale(1) rotate(180deg); }
        }
        @keyframes sparkle-drift {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(var(--drift-x), var(--drift-y)); }
        }
        .sparkle-glyph {
          position: absolute;
          will-change: transform, opacity;
          animation:
            sparkle-twinkle var(--twinkle-duration) ease-in-out infinite,
            sparkle-drift calc(var(--twinkle-duration) * 1.6) ease-in-out infinite;
          animation-delay: var(--anim-delay), calc(var(--anim-delay) * -1);
          line-height: 1;
          user-select: none;
        }
        @media (prefers-reduced-motion: reduce) {
          .sparkle-glyph { animation: none; opacity: 0.5; }
        }
      `}</style>
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          overflow: "hidden",
          zIndex: 0,
        }}
      >
        {sparkles.map((s) => (
          <span
            key={s.id}
            className="sparkle-glyph"
            style={{
              left: `${s.left}%`,
              top: `${s.top}%`,
              fontSize: `${s.size}px`,
              color,
              textShadow: `0 0 ${s.size}px ${color}`,
              "--twinkle-duration": `${s.duration}s`,
              "--anim-delay": `${s.delay}s`,
              "--drift-x": `${s.driftX}px`,
              "--drift-y": `${s.driftY}px`,
            }}
          >
            {s.glyph}
          </span>
        ))}
      </div>
    </>
  );
};

export default Sparkles;
